import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ParticleBurst } from '@/components/motion/ParticleBurst';
import { burstParticleCount, getMotionTier, shouldAnimateMotion, type BurstKind } from '@/lib/motion-effects';

type BurstRequest = {
  id: number;
  x: number;
  y: number;
  kind: BurstKind;
};

type MotionBurstContextValue = {
  spawnBurst: (x: number, y: number, kind?: BurstKind) => void;
};

const MotionBurstContext = createContext<MotionBurstContextValue | null>(null);

export function MotionBurstProvider({ children }: { children: ReactNode }) {
  const [bursts, setBursts] = useState<BurstRequest[]>([]);

  const spawnBurst = useCallback((x: number, y: number, kind: BurstKind = 'bubble') => {
    const tier = getMotionTier();
    if (!shouldAnimateMotion(tier)) return;
    if (burstParticleCount(tier, kind) === 0) return;

    const id = Date.now() + Math.random();
    setBursts((prev) => [...prev.slice(-2), { id, x, y, kind }]);
  }, []);

  const removeBurst = useCallback((id: number) => {
    setBursts((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const value = useMemo(() => ({ spawnBurst }), [spawnBurst]);

  return (
    <MotionBurstContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-0 z-[90]" aria-hidden>
        {bursts.map((b) => (
          <ParticleBurst key={b.id} x={b.x} y={b.y} kind={b.kind} onDone={() => removeBurst(b.id)} />
        ))}
      </div>
    </MotionBurstContext.Provider>
  );
}

export function useMotionBurst() {
  const ctx = useContext(MotionBurstContext);
  if (!ctx) {
    return { spawnBurst: () => undefined };
  }
  return ctx;
}
